import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { useOrganizationSettings } from 'utils/util/hooks';

const RICH_TEXT_VALUES = {
  b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
  ul: (chunks: React.ReactNode) => <ul className="mt-2 mb-0">{chunks}</ul>,
  li: (chunks: React.ReactNode) => <li className="mb-2">{chunks}</li>,
};

const ObjectivesSettingsAdminPage: FC = () => {
  const settings = useOrganizationSettings();
  const restrictVisibility =
    settings?.objectives_restrict_visibility_to_match_editability ?? false;

  return (
    <>
      <Card>
        <CardHeader>
          <h4 className="card-header-title">
            <FormattedMessage
              id="app.views.administration.objectives_admin.general.visibility_title"
              defaultMessage="Objectives visibility"
            />
          </h4>
        </CardHeader>
        <CardBody>
          {restrictVisibility ? (
            <FormattedMessage
              id="app.views.administration.objectives_admin.general.visibility_restricted"
              defaultMessage="Visibility is set to <b>hierarchical (chain-of-command)</b>. Users see objectives only on profiles they can access: their own, their direct reports, and people they admin via roles. Collaborators can always see objectives they're listed on, regardless of chain of command. The 'Add existing objectives' search and parent-objective linking are hidden. To switch to full visibility, contact customer support."
              values={RICH_TEXT_VALUES}
            />
          ) : (
            <FormattedMessage
              id="app.views.administration.objectives_admin.general.visibility_open"
              defaultMessage="Visibility is set to <b>full visibility</b>. Everyone in the organization can see all objectives across teams. The 'Add existing objectives' search and parent-objective linking are available to all users. To restrict visibility, contact customer support."
              values={RICH_TEXT_VALUES}
            />
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <h4 className="card-header-title">
            <FormattedMessage
              id="app.views.administration.objectives_admin.general.editability_title"
              defaultMessage="Who can edit an objective"
            />
          </h4>
        </CardHeader>
        <CardBody>
          <FormattedMessage
            id="app.views.administration.objectives_admin.general.editability_body"
            defaultMessage="<ul><li><b>Collaborators</b> can edit the objective and everything in its subtree. This applies to both strategic and individual objectives.</li><li>For <b>individual</b> objectives, managers and HR admins of any collaborator on the objective or any of its ancestors can also edit it.</li><li>For <b>strategic</b> objectives, administrators of the objective's type, or of any ancestor type in the link-parent hierarchy, can edit, even when they're not a collaborator. Authority cascades down through 'Allowed parent types'.</li><li><b>Individual</b> objectives must always have at least one collaborator. <b>Strategic</b> objectives may have zero. They're governed by their type administrators.</li><li>Users with the global <b>OKR Admin</b> role can edit any strategic objective in the organization.</li></ul>"
            values={RICH_TEXT_VALUES}
          />
        </CardBody>
      </Card>
    </>
  );
};

export default React.memo(ObjectivesSettingsAdminPage);
